import styles from '@/pages/app/BillingOps/styles.module.scss'

import { BillingOpsPreviewView } from '@/pages/app/BillingOps/types'
import { stripeDashboardUrl } from '@/utils/stripe'

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

interface PreviewPanelProps {
  preview: BillingOpsPreviewView
}

const PreviewPanel = ({ preview }: PreviewPanelProps) => {
  const isReversible = preview.reversibility === 'reversible'
  const badgeClass = `${styles.reversibilityBadge} ${isReversible ? styles.reversible : styles.irreversible}`

  return (
    <div className={styles.panel}>
      <div className={styles.section}>
        <div className={styles.actionRow}>
          <h2 className={styles.sectionTitle}>Preview</h2>
          <span className={badgeClass}>{isReversible ? 'REVERSIBLE' : 'IRREVERSIBLE'}</span>
        </div>
        <p className={styles.summary}>{preview.customer_impact.summary}</p>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>State changes</h3>
        {preview.state_diff.length === 0 ? (
          <p className={styles.empty}>No state changes.</p>
        ) : (
          <table className={styles.diffTable}>
            <thead>
              <tr>
                <th>Path</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {preview.state_diff.map((d, idx) => (
                <tr key={`${d.path}-${idx}`}>
                  <td>{d.path}</td>
                  <td>{formatValue(d.before)}</td>
                  <td>{formatValue(d.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Side effects</h3>
        {preview.side_effects.length === 0 ? (
          <p className={styles.empty}>No side effects.</p>
        ) : (
          <ul className={styles.list}>
            {preview.side_effects.map((s, idx) => (
              <li key={`${s.kind}-${idx}`}>
                <strong>{s.kind}:</strong> {s.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer artifacts</h3>
        {preview.customer_impact.artifacts.length === 0 ? (
          <p className={styles.empty}>None.</p>
        ) : (
          <div>
            {preview.customer_impact.artifacts.map((a, idx) => (
              <div key={`${a.kind}-${idx}`} className={styles.artifactItem}>
                <span className={styles.artifactKind}>{a.kind}</span>
                <span className={styles.artifactDescription}>{a.description}</span>
                {a.stripe_id && (
                  <a
                    className={styles.artifactStripeLink}
                    href={stripeDashboardUrl(a.stripe_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {a.stripe_id}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default PreviewPanel
